import React from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Users,
  MonitorPlay,
  TrendingUp,
  GraduationCap,
  Home,
  Clock,
  ClipboardList,
  Play,
  Video,
  Phone,
  MessageCircle,
  CheckCircle2,
  Megaphone,
  HelpCircle,
  Star,
  ArrowRight,
} from "lucide-react";
import OnlinePosterSlider from "@/components/app/OnlinePosterSlider";
import { KerjasamaStrip, JerseyBlock, MindsetGrid } from "@/components/app/OnlineClassExtras";
import TutorOnlineBlock from "@/components/app/TutorOnlineBlock";
import ManagerProfileCard from "@/components/app/ManagerProfileCard";

const IMG = (f) => `/landing/online/${f}`;

const FEATURES = [
  { icon: BookOpen, ms: ["Teknik Mengeja", "Langkah demi langkah"], en: ["Spelling Technique", "Step by step"] },
  { icon: Users, ms: ["Kelas Online Secara Live", "Kumpulan kecil"], en: ["Live Online Classes", "Small groups"] },
  { icon: MonitorPlay, ms: ["Aktiviti Menarik & Interaktif", "Lebih fokus, lebih faham"], en: ["Fun & Interactive Activities", "More focus, better understanding"] },
  { icon: TrendingUp, ms: ["Sesuai Untuk Umur 4-9 Tahun", "Asas yang kukuh"], en: ["Suitable For Ages 4-9", "A strong foundation"] },
];

const PROBLEMS = [
  { ms: "Umur 4-6 masih tak kenal huruf ABC?", en: "Aged 4-6 and still don't know ABC?" },
  { ms: "Masih tak pandai menulis?", en: "Still can't write?" },
  { ms: "Masih tak boleh membaca?", en: "Still can't read?" },
  { ms: "Tak fokus, mudah teralih perhatian?", en: "Can't focus, easily distracted?" },
];

const BOOKS = [
  { ms: "Kenal Huruf", en: "Letters", cls: "bg-navy-500 w-[92%]" },
  { ms: "Suku Kata", en: "Syllables", cls: "bg-blue-600 w-[86%] ml-2" },
  { ms: "Teknik Mengeja", en: "Spelling", cls: "bg-yellow-400 text-navy-950 w-[96%]" },
  { ms: "Membaca", en: "Reading", cls: "bg-orange-500 w-[88%] ml-1" },
  { ms: "Aktiviti Menarik", en: "Fun Activities", cls: "bg-emerald-600 w-full" },
];

const MASTER = [
  { ms: "Kenal Huruf", en: "Letter Recognition" },
  { ms: "Suku Kata", en: "Syllables" },
  { ms: "Mengeja", en: "Spelling" },
  { ms: "Kelancaran Membaca", en: "Reading Fluency" },
  { ms: "Lebih Yakin & Berani Membaca", en: "Confident & Brave Reader" },
];

const TILES = [
  { icon: GraduationCap, ms: "Tutor Berpengalaman", en: "Experienced Tutors" },
  { icon: Home, ms: "Belajar Dari Rumah", en: "Learn From Home" },
  { icon: Clock, ms: "Jadual Fleksibel", en: "Flexible Schedule" },
  { icon: ClipboardList, ms: "Pemantauan Pelajar", en: "Student Monitoring" },
];

export default function OnlineClassCard({ ms, cms, setLightbox, manager, managerPoster, posters }) {
  const L = (m, e) => (ms ? m : e);
  return (
    <div
      className="pmm-pop pmm-float-delayed min-w-0 overflow-hidden rounded-[2rem] border-4 border-blue-800 bg-white shadow-2xl shadow-blue-800/20 transition-shadow duration-300 hover:shadow-blue-800/40"
      style={{ animationDelay: "0.25s" }}
      data-testid="pilihan-card-online"
    >
      {/* ===== Header (navy + gold, like poster) ===== */}
      <div className="relative overflow-hidden bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800 px-6 pb-8 pt-7 text-center sm:px-8">
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-yellow-400/20 blur-3xl" />
        <div className="pointer-events-none absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-blue-600/25 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-yellow-600 via-yellow-300 to-yellow-600" />
        <span className="relative inline-block rounded-full bg-blue-700 px-6 py-1.5 font-display text-sm font-bold uppercase tracking-widest text-white shadow-md">
          {L("Kelas", "Class")}
        </span>
        <p className="relative mt-3 font-display text-[11px] font-extrabold uppercase tracking-[0.25em] text-yellow-300">
          Program Membaca PMM
        </p>
        <h3 className="relative mt-1 font-display text-3xl font-extrabold uppercase leading-none tracking-tight text-transparent sm:text-4xl" style={{ backgroundImage: "linear-gradient(180deg,#f5e08d,#f0a500)", WebkitBackgroundClip: "text", backgroundClip: "text" }}>
          {L("Kelas Membaca", "Reading Class")}
        </h3>
        <div className="relative mx-auto mt-3 inline-flex items-center gap-3 rounded-full border-2 border-yellow-400 bg-gradient-to-r from-blue-700 to-blue-500 py-1.5 pl-2 pr-6 shadow-xl shadow-blue-700/40">
          <span className="grid h-10 w-10 place-items-center rounded-full bg-white text-blue-700 shadow-inner">
            <Play className="ml-0.5 h-5 w-5 fill-blue-700" />
          </span>
          <span className="font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">Online</span>
        </div>
        <p className="relative mt-3 font-script text-2xl text-yellow-200">
          {L("\u201CAnak Mesti Lancar Membaca\u201D", "\u201CEvery Child Must Read Fluently\u201D")}
        </p>
        <div className="relative mx-auto mt-3 inline-block rounded-lg bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white ring-1 ring-yellow-400/50 backdrop-blur sm:text-sm">
          {L("Belajar Dari Rumah Bersama Tutor Bertauliah", "Learn From Home With Certified Tutors")}
        </div>
        <div className="relative mt-4 flex flex-wrap justify-center gap-2">
          {["Zoom Meeting", "Google Meet"].map((p) => (
            <span key={p} className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-bold text-navy-900 shadow" data-testid={`online-platform-${p.split(" ")[0].toLowerCase()}`}>
              <Video className="h-3.5 w-3.5 text-blue-700" /> {p}
            </span>
          ))}
        </div>
      </div>

      <div className="space-y-7 p-6 sm:p-8">
        {/* ===== Hero visual ===== */}
        <div className="relative pb-6" data-testid="online-hero-visual">
          <div className="pmm-cinematic relative overflow-hidden rounded-2xl border-4 border-yellow-400 shadow-2xl">
            <img src={cms.online_image || IMG("kids-online.jpg")} alt={L("Kanak-kanak belajar membaca secara online", "Children learning to read online")} className="h-auto w-full transition-transform duration-700 hover:scale-105" loading="lazy" />
          </div>
          <div className="absolute -right-2 -top-4 rotate-6">
            <div className="pmm-pop rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 px-3 py-2 text-center font-display text-[11px] font-extrabold uppercase leading-tight text-white shadow-xl ring-2 ring-white sm:text-xs">
              {L("Anak Boleh Membaca", "Kids Can Read")}
              <span className="block text-yellow-300">{L("Lebih Yakin!", "With Confidence!")}</span>
            </div>
          </div>
          <div className="absolute -bottom-1 left-3 flex items-center gap-2 rounded-full bg-navy-950 px-4 py-2 font-display text-xs font-extrabold uppercase tracking-wide text-white shadow-xl ring-2 ring-yellow-400">
            <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
            {L("Umur", "Ages")} <span className="text-yellow-300">4-9 {L("Tahun", "Years")}</span>
          </div>
          <div className="absolute -bottom-2 right-3 w-24 -rotate-3 overflow-hidden rounded-xl border-4 border-white shadow-2xl transition-transform duration-300 hover:rotate-0 hover:scale-110 sm:w-28">
            <img src={IMG("boy-online.jpg")} alt={L("Murid PMM kelas online", "PMM online student")} className="h-auto w-full" loading="lazy" />
          </div>
        </div>

        {/* ===== 4 key features ===== */}
        <ul className="space-y-2.5" data-testid="online-features">
          {FEATURES.map((f, i) => (
            <li key={f.en[0]} className="pmm-pop group flex items-center gap-3 rounded-full bg-gradient-to-r from-navy-900 to-navy-700 p-1.5 pr-5 shadow-lg transition-transform duration-300 hover:translate-x-1" style={{ animationDelay: `${0.1 + i * 0.08}s` }}>
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-navy-950 text-yellow-300 ring-2 ring-yellow-400">
                <f.icon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="font-display text-sm font-extrabold uppercase leading-tight text-white">{ms ? f.ms[0] : f.en[0]}</div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-yellow-200/90">{ms ? f.ms[1] : f.en[1]}</div>
              </div>
            </li>
          ))}
        </ul>

        {/* ===== Real online session ===== */}
        <div className="overflow-hidden rounded-2xl border-2 border-navy-100 bg-white shadow-lg" data-testid="online-session-block">
          <div className="relative">
            <button type="button" onClick={() => setLightbox("/landing/pmm/poster-online-sesi.jpg")} className="block w-full overflow-hidden" data-testid="online-session-photo">
              <img src="/landing/pmm/online-session.jpg" alt={L("Sesi sebenar kelas online PMM", "A real PMM online class session")} className="h-auto w-full transition-transform duration-700 hover:scale-105" loading="lazy" />
            </button>
            <div className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-navy-950/85 via-navy-950/40 to-transparent px-4 pb-10 pt-3">
              <p className="font-display text-2xl font-extrabold uppercase leading-none text-white sm:text-3xl">{L("Sesi ", "PMM ")}<span className="text-yellow-300">{L("Kelas", "Online")}</span>{L(" Online PMM", " Session")}</p>
              <span className="mt-2 inline-block -skew-x-12 border-l-4 border-yellow-400 bg-navy-900 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wide text-white sm:text-xs">
                <span className="inline-block skew-x-12">{L("Belajar Seronok, Membaca Dengan Yakin!", "Fun Learning, Confident Reading!")}</span>
              </span>
            </div>
          </div>
          <div className="grid grid-cols-3 divide-x divide-navy-100">
            {[
              { icon: BookOpen, t: L("Sistem Terbukti", "Proven System") },
              { icon: Users, t: L("Dibangunkan Oleh Pakar Pendidikan", "Built By Education Experts") },
              { icon: Star, t: L("Berkesan & Menyeluruh", "Effective & Thorough") },
            ].map((x) => (
              <div key={x.t} className="flex flex-col items-center gap-1.5 px-1 py-3 text-center">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-navy-900 text-yellow-300"><x.icon className="h-4 w-4" /></span>
                <span className="text-[9px] font-extrabold uppercase leading-tight text-navy-900 sm:text-[11px]">{x.t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ===== Tutor Kelas Online PMM ===== */}
        <TutorOnlineBlock ms={ms} setLightbox={setLightbox} />

        {/* ===== Parent worries -> solution ===== */}
        <div className="rounded-2xl border-2 border-blue-200 bg-blue-50/60 p-4" data-testid="online-problems">
          <p className="text-center font-display text-base font-extrabold uppercase leading-tight text-navy-900">
            {L("Ibubapa ", "Parents ")}<span className="text-blue-700">{L("\u201CRisau\u201D", "\u201CWorried\u201D")}</span>{L(" Anak…", " Their Child…")}
          </p>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {PROBLEMS.map((p) => (
              <div key={p.en} className="flex items-start gap-2 rounded-xl bg-white px-3 py-2 text-xs font-bold text-navy-900 shadow-sm">
                <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" /> {ms ? p.ms : p.en}
              </div>
            ))}
          </div>
          <p className="mt-3 rounded-2xl bg-gradient-to-r from-blue-700 to-blue-500 px-4 py-2 text-center font-display text-xs font-extrabold uppercase leading-snug tracking-wide text-white shadow-md sm:rounded-full sm:text-sm">
            <Megaphone className="mr-1.5 inline h-4 w-4 -translate-y-px text-yellow-300" />
            {L("Segera jumpa kami bantu, ", "Reach us now, ")}<span className="text-yellow-300">{L("kelas hampir penuh!", "classes almost full!")}</span>
          </p>
        </div>

        {/* ===== Books + mastery checklist ===== */}
        <div className="grid grid-cols-5 items-end gap-3">
          <div className="col-span-2 space-y-1" data-testid="online-book-stack">
            {BOOKS.map((b, i) => (
              <div key={b.en} className={`pmm-pop rounded-r-md rounded-l-sm px-2 py-1.5 font-display text-[10px] font-extrabold uppercase leading-none text-white shadow-md sm:text-xs ${b.cls}`} style={{ animationDelay: `${0.1 + i * 0.07}s` }}>
                {ms ? b.ms : b.en}
              </div>
            ))}
          </div>
          <div className="col-span-3 -rotate-1 rounded-2xl bg-gradient-to-br from-yellow-200 via-yellow-300 to-yellow-400 p-4 shadow-lg" data-testid="online-mastery">
            <p className="mb-2 font-display text-[11px] font-extrabold uppercase tracking-widest text-navy-900">{L("Anak akan kuasai", "Your child will master")}</p>
            <ul className="space-y-1.5">
              {MASTER.map((m) => (
                <li key={m.en} className="flex items-center gap-2 text-xs font-bold text-navy-950 sm:text-sm">
                  <CheckCircle2 className="h-4 w-4 shrink-0 fill-blue-700 text-white" /> {ms ? m.ms : m.en}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ===== Result 3-4 months ===== */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-navy-950 to-navy-800 p-5 text-center shadow-xl" data-testid="online-result">
          <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-yellow-400/20 blur-2xl" />
          <p className="relative font-display text-sm font-extrabold uppercase tracking-wide text-white">{L("Anak boleh lancar membaca dalam", "Your child can read fluently in")}</p>
          <p className="relative font-display text-5xl font-extrabold uppercase leading-none text-transparent" style={{ backgroundImage: "linear-gradient(180deg,#f5e08d,#f0a500)", WebkitBackgroundClip: "text", backgroundClip: "text" }}>
            3-4 <span className="text-3xl">{L("Bulan", "Months")}</span>
          </p>
          <div className="relative mt-3 flex flex-col items-center gap-1.5 text-xs font-bold text-white sm:text-sm">
            <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" />{L("Cikgu ajar sampai lancar membaca sebelum Darjah 1", "Teachers coach until fluent before Year 1")}</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" />{L("Cikgu fokus secara individu", "Individual attention from teachers")}</span>
          </div>
        </div>

        {/* ===== 4 benefit tiles ===== */}
        <div className="grid grid-cols-4 gap-2 text-center" data-testid="online-tiles">
          {TILES.map((f) => (
            <div key={f.en} className="flex min-w-0 flex-col items-center gap-1.5 rounded-xl bg-navy-900 px-1 py-3 shadow-md transition-transform duration-300 hover:-translate-y-1">
              <f.icon className="h-6 w-6 text-yellow-300" />
              <span className="break-words text-[8px] font-bold uppercase leading-tight text-white [overflow-wrap:anywhere] sm:text-[11px]">{ms ? f.ms : f.en}</span>
            </div>
          ))}
        </div>

        {/* ===== Hotline + CTA ===== */}
        <div className="space-y-3" data-testid="online-hotline-block">
          <a href="tel:+60178730024" className="flex items-center gap-3 rounded-full border-2 border-yellow-400 bg-gradient-to-r from-blue-800 via-blue-700 to-blue-600 p-1.5 pr-6 shadow-xl shadow-blue-700/30 transition-transform duration-300 hover:scale-[1.02]" data-testid="online-hotline">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-gradient-to-br from-yellow-200 to-yellow-500 text-blue-800 shadow-inner">
              <Phone className="h-6 w-6" />
            </span>
            <span className="min-w-0">
              <span className="block font-display text-xs font-extrabold uppercase tracking-widest text-yellow-300">Hotline PMM</span>
              <span className="num block font-display text-2xl font-extrabold text-white sm:text-3xl">017-8730024</span>
            </span>
          </a>
          <Link to="/register" className="pmm-atau flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-yellow-300 to-yellow-500 px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wide text-navy-950 shadow-lg transition-transform duration-300 hover:scale-[1.03]" data-testid="online-register-cta">
            {L("Daftar Sekarang! Tempat Terhad!", "Register Now! Limited Seats!")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* ===== Online manager (berkod dari poster Cikgu Zarith) ===== */}
        <ManagerProfileCard
          ms={ms}
          manager={manager}
          onOpen={setLightbox}
          photo="/landing/online/zarith.webp"
          poster="/landing/online/poster-zarith.jpg"
          fullName={["Nurzarith Sofea", "Binti Hamidon"]}
          role={{ ms: "Penolong Pengurus", en: "Assistant Manager" }}
          title={L("Pengurus Kelas Online PMM Malaysia", "PMM Malaysia Online Class Manager")}
          subtitle={L("Info detail kelas, yuran & jadual — WhatsApp pengurus PMM", "Class details, fees & schedule — WhatsApp the PMM manager")}
          testId="online-manager"
        />

        {/* ===== CEO & Founder (berkod dari poster Norhayati Setta) ===== */}
        <ManagerProfileCard
          ms={ms}
          manager={{ name: "Norhayati Setta" }}
          onOpen={setLightbox}
          photo="/landing/online/ceo.webp"
          photoClass="sm:right-[-14%] sm:h-[86%]"
          poster="/landing/online/poster-ceo.jpg"
          fullName={["Norhayati", "Setta"]}
          nameSerif
          role={{ ms: "CEO & Founder", en: "CEO & Founder" }}
          title={L("CEO & Pengasas PMM", "PMM CEO & Founder")}
          subtitle={L("\u201CSetiap Anak Boleh Membaca\u201D", "\u201CEvery Child Can Read\u201D")}
          badge="CEO & Founder"
          cta={{ href: "tel:+60167202689", label: L("Hotline PMM 016-7202689", "PMM Hotline 016-7202689"), kind: "tel" }}
          testId="online-founder"
        />
        <KerjasamaStrip ms={ms} setLightbox={setLightbox} />
        <JerseyBlock ms={ms} setLightbox={setLightbox} />
        <MindsetGrid ms={ms} setLightbox={setLightbox} />

        {/* ===== Poster gallery ===== */}
        <div>
          <p className="mb-3 text-center font-display text-xs font-extrabold uppercase tracking-widest text-blue-800">{L("Galeri Poster Kelas Online", "Online Class Poster Gallery")}</p>
          <OnlinePosterSlider posters={posters} onOpen={setLightbox} ms={ms} contain />
        </div>
      </div>

      {/* ===== Footer strip ===== */}
      <div className="bg-navy-950 px-6 py-4 text-center">
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-white/80 sm:text-xs">
          <span>{L("Membaca Ilmu", "Reading Is Knowledge")}</span><span className="text-yellow-400">•</span>
          <span>{L("Mengubah Masa Depan", "Changing The Future")}</span><span className="text-yellow-400">•</span>
          <span>{L("Generasi Lebih Hebat", "A Greater Generation")}</span>
        </div>
        <p className="mt-1 font-script text-lg text-yellow-300">{L("Bersama Membina Generasi Celik Membaca", "Building A Literate Generation Together")}</p>
      </div>
    </div>
  );
}
